import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard } from '../api/guard'
import './screens.css'

interface SystemSetting {
  key: string
  value: unknown
  description: string | null
  updated_at: string | null
}
interface SettingsResponse {
  settings: SystemSetting[]
}

type PendingEdit = { key: string; value: string }

export function SystemSettingsView() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<PendingEdit | null>(null)

  const query = useQuery<SettingsResponse>({
    queryKey: ['admin-system-settings'],
    queryFn: () => apiFetch<SettingsResponse>('/admin/system-settings'),
  })
  useForbiddenGuard(query.error)

  const mutation = useMutation({
    mutationFn: (vars: { key: string; value: unknown }) =>
      apiFetch(`/admin/system-settings/${encodeURIComponent(vars.key)}`, {
        method: 'PATCH',
        body: JSON.stringify({ value: vars.value }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-system-settings'] }),
  })
  useForbiddenGuard(mutation.error)

  const settings = query.data?.settings ?? []

  return (
    <section className="ss" aria-labelledby="ss-heading">
      <div className="screen-head">
        <h2 id="ss-heading">System Settings</h2>
      </div>

      {mutation.isError && (mutation.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Update failed{(mutation.error as { status?: number }).status === 404 ? ' — setting not found.' : '.'}
        </p>
      )}

      <table className="ss-table">
        <thead>
          <tr>
            <th>Key</th>
            <th>Value</th>
            <th>Description</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {settings.map((s) => (
            <tr key={s.key}>
              <td>{s.key}</td>
              <td>
                <code>{JSON.stringify(s.value)}</code>
              </td>
              <td>{s.description ?? '—'}</td>
              <td>
                <button
                  type="button"
                  className="screen-btn"
                  aria-label={`Edit ${s.key}`}
                  onClick={() => setEditing({ key: s.key, value: JSON.stringify(s.value) })}
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <div className="screen-confirm-backdrop" role="dialog" aria-modal="true" aria-label="Edit setting">
          <div className="screen-confirm">
            <label htmlFor="ss-value">
              Value for <strong>{editing.key}</strong>
            </label>
            <input
              id="ss-value"
              className="ws-search"
              value={editing.value}
              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
            />
            <div className="screen-confirm-actions">
              <button type="button" className="screen-btn" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="screen-btn screen-btn-primary"
                onClick={() => {
                  // Store the raw string; parse to JSON when it is valid JSON, else keep as string.
                  let parsed: unknown = editing.value
                  try {
                    parsed = JSON.parse(editing.value)
                  } catch {
                    /* keep as string */
                  }
                  mutation.mutate({ key: editing.key, value: parsed })
                  setEditing(null)
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
